import { useState } from 'react';
import type { ContactFormData, ContactResponse } from '../types';
import { config } from '../config';
import { useRecaptcha } from './useRecaptcha';

export function useContactForm() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { executeRecaptcha } = useRecaptcha();

  const submitForm = async (data: ContactFormData): Promise<void> => {
    setIsLoading(true);
    setError(null);

    try {
      // Get reCAPTCHA token
      const recaptchaToken = await executeRecaptcha('contact_submit');
      
      if (!recaptchaToken) {
        throw new Error('reCAPTCHA verification failed. Please try again.');
      }

      // Submit form data
      const response = await fetch(`${config.apiBase}/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...data,
          recaptchaToken,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Network error' }));
        throw new Error(errorData.message || `HTTP error ${response.status}`);
      }

      const result: ContactResponse = await response.json();
      
      if (!result.success) {
        throw new Error(result.message || 'Form submission failed');
      }

      // Success - the form component will handle UI updates
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An unexpected error occurred';
      setError(errorMessage);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  return {
    submitForm,
    isLoading,
    error,
  };
}
